/*
 * flashmeeting-fe
 *
 * Author: Andgel BRISSAUD
 * Copyright (c) 2024 Prodeo - All Rights Reserved
 */

/* ===== External Libraries ===== */
import { AXIOS, CallbackErrorAxios, CallbackWarningAxios, CommonGlobalService, dayjs, FORMAT_DATE_API, i18n, UUID } from '@umanforge/common';
import { User } from 'interfaces/user.interface';
import { AxiosResponse, AxiosError } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

/* ===== API Configuration ===== */
import { API } from 'constants/api.constant';

/* ===== Types and Interfaces ===== */
import { SiteTableResponse, TableCreation } from 'interfaces/types.interface';
import { AdminTableApiResponse, AdminTableWithSitesApiResponse, ApiParticipant, EventManager, EventManagerWithPosition, EventManagerWithPositionBySector, Participant, UpdateTablePositionPayload } from 'interfaces/eventManager.interface';

/* ===== Services ===== */
import { GlobalService } from './global.service';

/* ===== Components ===== */
import { DEFAULT_BACKLOG } from 'constants/eventManager.constant';
import { PutBodyHolidayLocation } from 'interfaces/holidayLocation.interface';
import { PostClosedDayToEventManager } from 'interfaces/closedDays.interface';

export class EventManagerService {

  /* ===== Send Request to update eventManager ===== */
  static updateEventManager(
    eventManagerId: UUID, body: TableCreation,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( `${API.EVENTMANAGERS}/${eventManagerId}`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send Request to update eventManager position ===== */
  static updateTablePositions(
    sectorId: string, eventManagers: UpdateTablePositionPayload['eventManagers'],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ) {
    return new Promise( async resolve => {
      const body: UpdateTablePositionPayload = {
        sectorId,
        eventManagers,
      };

      AXIOS.put( `${API.EVENTMANAGERS}`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send Request to fetch all eventManagers on a sector ===== */
  static getTablesBySector(
    sectorId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EventManagerWithPosition[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.EVENTMANAGERS}/${sectorId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<EventManagerWithPosition[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send Request to fetch all eventManagers by a list of sector IDs ===== */
  static getTablesBySectorIDs(
    sectorIds: string[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EventManagerWithPositionBySector[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.EVENTMANAGERS}/sector/bySectorIDs`, { ...await CommonGlobalService.getHeaderRequest( token ), params: { sectorIds } } )
        .then( ( response: AxiosResponse<{ sectorId: UUID, eventManagers: EventManagerWithPosition[] }[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          const result = response.data.map( data => { return data.eventManagers.map( eventManager => { return { ...eventManager, sectorId: data.sectorId }; } ); } ).flat();
          resolve( result );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send Request to fetch all eventManagers on a list of sites ===== */
  static getTablesBySiteIDs(
    siteIds: string[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<SiteTableResponse> {
    return new Promise( async resolve => {
      AXIOS.post( `${API.EVENT_MANAGERS_BY_SITE}`, { siteIds }, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<SiteTableResponse> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send Request to fetch an eventManager by his id ===== */
  static getEventManagerById(
    eventManagerId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EventManager> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.EVENTMANAGERS}/id/${eventManagerId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<EventManager> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send Request to fetch all event managers where user is participant or admin ===== */
  static getEventManagersWhereUserIsParticipantOrAdmin(
    userId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<AdminTableWithSitesApiResponse[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.EVENTMANAGERS}/user/${userId}/participant-or-admin`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<AdminTableWithSitesApiResponse[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send Request to fetch all event managers where user is participant or admin (for extraction, with read_access_sectors) ===== */
  static getEventManagersWhereUserIsParticipantOrAdminForExtraction(
    userId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<AdminTableWithSitesApiResponse[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.EVENTMANAGERS}/user/${userId}/participant-or-admin-for-extraction`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<AdminTableWithSitesApiResponse[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }
  /* ===== Send Request to fetch all event managers where user is admin ===== */

  /* ===== Get all missing days of an event manager grouped by user ===== */
  static getMissingDayOfEventManagerByfUser(
    eventManagerId: string, startDate: dayjs.Dayjs, endDate: dayjs.Dayjs,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<{ [userId: UUID]: { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs; }[] }> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.EVENTMANAGERS}/${eventManagerId}/closed-days-by-user/${startDate.format( FORMAT_DATE_API )}/${endDate.format( FORMAT_DATE_API )}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse< { [userId: UUID]: { startDate: string; endDate: string; }[] }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          const keys = Object.keys( response.data );
          const missingDays: { [userId: UUID]: { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs; }[] } = {};
          for ( const key of keys ) {
            missingDays[ key ] = response.data[ key ].map( range => {
              return { startDate: dayjs( range.startDate, FORMAT_DATE_API ), endDate: dayjs( range.endDate, FORMAT_DATE_API ) };
            } );
          }
          resolve( missingDays );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send Request to fetch all users that is in the event managers ===== */
  static getUsersByEventManagerIds(
    eventManagerIds: string[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<User[]> {
    return new Promise( async resolve => {
      AXIOS.post( `${API.EVENTMANAGERS}/common/byEventManagerIDs`, { eventManagerIds }, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<AdminTableApiResponse[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Send Request to delete an eventManager ===== */
  static deleteTable(
    eventManagerId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( `${API.EVENTMANAGERS}/${eventManagerId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  /* ===== Send Request to create a new eventManager ===== */
  static createTable(
    body: TableCreation,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UUID | undefined> {
    return new Promise( async resolve => {
      AXIOS.post( API.EVENTMANAGERS, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data.id );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getTableParticipants = (
    eventManagerIds: string[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Participant[]> => {
    return new Promise( async resolve => {
      AXIOS.post( `${API.EVENTMANAGERS}/participants`, { eventManagerIds }, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<ApiParticipant[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( EventManagerService.formatedBacklogForParticipants( response.data ) );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  };

  static formatedBacklogForParticipants = ( apiData: ApiParticipant[] ): Participant[] => {
    const users = apiData.map( item => {
      return {
        id: item.userId,
        eventManagerId: item.eventManagerId,
        firstname: item.firstName,
        lastname: item.lastName,
        position: Number( item.position ),
        isAdmin: item.isAdmin,
      };
    } );

    const usersFiltered = Array.from( new Set( users.map( user => { return user.id; } ) ) )
      .map( userId => { return users.find( user => { return user.id === userId && user.id !== DEFAULT_BACKLOG.ID; } ); } )
      .filter( user => { return user != null; } );
    usersFiltered.push( ...users.filter( user => { return user.id === DEFAULT_BACKLOG.ID; } ) );

    return users.map( user => {
      const firstname = (
        user.id === DEFAULT_BACKLOG.ID ?
          ( user.firstname === DEFAULT_BACKLOG.NAME ? i18n.t( user.firstname ) : user.firstname )
          :
          user.firstname
      );
      user.firstname = firstname;
      return user;
    } );
  };

  static postClosedDaysToEventManager(
    eventManagerId: string, closedDays: PostClosedDayToEventManager[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( `${API.EVENTMANAGERS}/${ eventManagerId }/closed-days`, { closedDays }, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteClosedDaysToEventManager(
    closedDaysId: UUID[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( `${API.EVENTMANAGERS}/closed-days`, { ...await CommonGlobalService.getHeaderRequest( token ), params: { closedDaysId } } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static putLocalisationOfHolidayToEventManager(
    eventManagerId: string, body: PutBodyHolidayLocation,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( `${API.EVENTMANAGERS}/${ eventManagerId }/holidays-location`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteLocalisationOfHolidayToEventManager(
    eventManagerId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( `${API.EVENTMANAGERS}/${ eventManagerId }/holidays-location`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static postExcludedHolidayToEventManager(
    eventManagerId: string, rules: string[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( `${API.EVENTMANAGERS}/${ eventManagerId }/holidays`, { rules }, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteExcludedHolidayToEventManager(
    eventManagerId: string, rules: string[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( `${API.EVENTMANAGERS}/${ eventManagerId }/holidays`, { ...await CommonGlobalService.getHeaderRequest( token ), params: { rules } } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getAllAbsenceDaysByEventManagerId(
    eventManagerId: string, range: { start: string, end: string },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<{ startDate: string, endDate: string }[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.EVENTMANAGERS}/absence-days`, { ...await CommonGlobalService.getHeaderRequest( token ), params: { eventManagerIds: [ eventManagerId ], range } } )
        .then( ( response: AxiosResponse<{ [ eventManagerId: UUID ]: { startDate: string, endDate: string }[] }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data[ eventManagerId ] ?? [] );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }


  static getOrderedParticipantsOfEventManager = ( eventManagerWithParticipants: EventManager, connectedUser: User | null ) => {
    const orderedParticipants = eventManagerWithParticipants.participants
      .sort( ( p1, p2 ) => {
        return ( p1.position ?? 1000 ) > ( p2.position ?? 1000 ) ? 1 : -1;
      } )
      .map( user => {
        if ( !user.eventManagersId ) {
          user.eventManagersId = [ eventManagerWithParticipants.id ];
        }
        return user as ( User & { eventManagersId: UUID[]; } );
      } );
    if ( connectedUser ) {
      const index = orderedParticipants.findIndex( participant => { return participant.id === connectedUser.id; } );
      if ( index != -1 ) {
        const meAsParticipant = orderedParticipants[ index ];
        orderedParticipants.splice( index, 1 );
        orderedParticipants.splice( 0, 0, meAsParticipant );
      }
    }
    return orderedParticipants;
  };
}
